/**
 * `errorLookup` — inline Error-ID resolution for the Ledgiventa assistant
 * (help-docs-panel, Requirement 11).
 *
 * When a user types a bare Error ID into the {@link ChatbotDock} (nothing
 * else on the line — just the ID), the dock short-circuits the normal
 * chatbot transport and resolves the ID against the owner-scoped
 * `GET /errors/{errorId}` endpoint owned by the `error-handling-observability`
 * spec. The stored record is then formatted into a {@link ChatbotResponse}
 * and rendered as an assistant message, with the error-handling docs cited.
 *
 * ## Why this does NOT go through the chatbot transport
 *
 * The chatbot transport (`Stub_Chatbot_Transport` today, the deferred MCP
 * client later) is tenant-isolated: it only ever receives
 * `{ sessionId, message, context }` and NEVER `ownerId` or `authorization`
 * (Constitution Rule II, property test P5). The error lookup, by contrast,
 * is an authenticated, owner-scoped read that must reach the backend with
 * the caller's Clerk identity. It therefore uses the shared `api` client
 * (which attaches the Clerk JWT + `x-clerk-subdomain` via interceptors and
 * lets the backend resolve `ownerId` server-side) — exactly like every
 * other authenticated call in the app. The transport contract is left
 * untouched.
 *
 * ## ID format
 *
 * Both Error-ID forms in the platform are RFC-4122 UUIDs:
 *   - client-generated v4 (e.g. dashboard widget fetch failures), and
 *   - server-issued v5 from `ErrorIdGenerator` (deterministic hash).
 * A single anchored UUID regex therefore matches both. The match is
 * whole-string only: a message that merely *contains* a UUID (with other
 * words around it) is treated as a normal chatbot message, not a lookup.
 *
 * @see .kiro/specs/help-docs-panel/requirements.md — Requirement 11
 * @see .kiro/specs/help-docs-panel/design.md — § Component "errorLookup"
 * @see backend/src/lambdas/error-handler/src/index.ts — GET /errors/{errorId}
 * @see ../services/api.ts — the authenticated, owner-scoped client used here
 *
 * @packageDocumentation
 */

import api from "../services/api";
import type { ChatbotResponse } from "./StubChatbotTransport";

/**
 * Anchored RFC-4122 UUID pattern (versions 1–5, variant 8–b).
 *
 * @remarks
 * Anchored with `^`/`$` so it only matches a string that is *exactly* a
 * UUID and nothing else. Case-insensitive because both the browser
 * (`crypto.randomUUID`, lowercase) and `uuidv5` emit lowercase, but a
 * user may paste an uppercased copy.
 */
const ERROR_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * The error-handling docs cited on every lookup response so the reader
 * always has a next step. Manifest-relative (no leading `/`, ends `.md`)
 * to match the {@link ChatbotResponse.citedDocs} contract.
 */
const ERROR_HANDLING_DOCS: readonly string[] = Object.freeze([
  "error-handling/guide.md",
  "error-handling/categories.md",
]);

/**
 * Decide whether a chatbot message is a bare Error-ID lookup request.
 *
 * @param message - The raw text the user typed into the dock.
 * @returns The normalised (lowercased) Error ID when the *entire* trimmed
 *   message is a single valid UUID; otherwise `null`.
 *
 * @remarks
 * This is the "regex check for a valid error code string entered by
 * itself" that gates the lookup. Leading/trailing whitespace is ignored,
 * but any additional words make it a normal chatbot message (returns
 * `null`) so questions like "what does error 03d8… mean?" still go to the
 * transport.
 *
 * @example
 * ```ts
 * parseErrorIdQuery("03d8186b-3b0e-475c-96a2-dec641dafb72"); // → the id
 * parseErrorIdQuery("  03D8186B-3B0E-475C-96A2-DEC641DAFB72 "); // → lowercased id
 * parseErrorIdQuery("what is 03d8186b-3b0e-475c-96a2-dec641dafb72?"); // → null
 * parseErrorIdQuery("hello"); // → null
 * ```
 */
export function parseErrorIdQuery(message: string): string | null {
  if (typeof message !== "string") return null;
  const trimmed = message.trim();
  if (!ERROR_ID_PATTERN.test(trimmed)) return null;
  return trimmed.toLowerCase();
}

/**
 * The subset of the stored ErrorLog record the assistant renders. All
 * fields are optional because the record is validated server-side, not
 * here — we read defensively and omit anything absent.
 *
 * @remarks
 * Mirrors the item shape written by the error-handler Lambda's
 * `POST /errors` path (category, severity, userMessage, technicalMessage,
 * timestamp, requestPath, requestMethod, responseStatus, errorType).
 */
interface StoredErrorRecord {
  errorId?: unknown;
  category?: unknown;
  severity?: unknown;
  errorType?: unknown;
  userMessage?: unknown;
  technicalMessage?: unknown;
  timestamp?: unknown;
  requestMethod?: unknown;
  requestPath?: unknown;
  responseStatus?: unknown;
}

/** Read a string field, returning `undefined` when absent/non-string/empty. */
function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Format a resolved ErrorLog record into the assistant's message body.
 * Only lines whose underlying field is present are emitted, so a sparse
 * record still renders cleanly.
 */
function formatErrorRecord(errorId: string, record: StoredErrorRecord): string {
  const lines: string[] = [`Error ${errorId}`];

  const category = readString(record.category);
  const severity = readString(record.severity);
  if (category !== undefined || severity !== undefined) {
    const parts: string[] = [];
    if (category !== undefined) parts.push(`Category: ${category}`);
    if (severity !== undefined) parts.push(`Severity: ${severity}`);
    lines.push(parts.join(" · "));
  }

  const errorType = readString(record.errorType);
  if (errorType !== undefined) lines.push(`Type: ${errorType}`);

  const timestamp = readString(record.timestamp);
  if (timestamp !== undefined) lines.push(`When: ${timestamp}`);

  const userMessage = readString(record.userMessage);
  if (userMessage !== undefined) lines.push(`Message: ${userMessage}`);

  const technicalMessage = readString(record.technicalMessage);
  if (technicalMessage !== undefined && technicalMessage !== userMessage) {
    lines.push(`Technical: ${technicalMessage}`);
  }

  const method = readString(record.requestMethod);
  const path = readString(record.requestPath);
  if (path !== undefined) {
    const status =
      typeof record.responseStatus === "number" && record.responseStatus > 0
        ? ` (status ${record.responseStatus})`
        : "";
    lines.push(`Where: ${method ? `${method} ` : ""}${path}${status}`);
  }

  return lines.join("\n");
}

/** Assistant body shown when the endpoint returns a 200 with a `null` body. */
const NOT_FOUND_TEXT = (errorId: string): string =>
  `I couldn't find a stored record for error ${errorId}. This usually means the ID was generated in the browser for a request that failed before the server logged anything (for example a network or CORS failure), so there's nothing on the server to look up. Check the browser's Network tab for the failing request, and see the error-handling docs below.`;

/** Assistant body shown when the lookup request itself fails. */
const LOOKUP_FAILED_TEXT = (errorId: string): string =>
  `I couldn't reach the error service to look up ${errorId} just now. Please try again in a moment, or check the error-handling docs below.`;

/**
 * Resolve an Error ID against the owner-scoped `GET /errors/{errorId}`
 * endpoint and format the outcome as a {@link ChatbotResponse}.
 *
 * @param errorId - A normalised Error ID (as returned by
 *   {@link parseErrorIdQuery}).
 * @returns A promise that always resolves to a {@link ChatbotResponse} —
 *   never rejects, mirroring the chatbot transport contract so the dock's
 *   render path is uniform.
 *
 * Three outcomes:
 *   1. **Found** — the endpoint returns the stored record → a formatted
 *      summary (category, severity, message, timestamp, request context).
 *   2. **Not found** — the endpoint returns `200` with a `null` body → a
 *      message explaining the ID was likely client-generated and never
 *      persisted.
 *   3. **Failed** — the request rejects → a graceful retry message.
 *
 * All three cite the error-handling docs.
 */
export async function lookupErrorById(
  errorId: string,
): Promise<ChatbotResponse> {
  try {
    const response = await api.get(`/errors/${encodeURIComponent(errorId)}`);
    const record = (response as { data?: unknown } | undefined)?.data;

    if (record === null || record === undefined || typeof record !== "object") {
      return {
        text: NOT_FOUND_TEXT(errorId),
        citedDocs: [...ERROR_HANDLING_DOCS],
      };
    }

    return {
      text: formatErrorRecord(errorId, record as StoredErrorRecord),
      citedDocs: [...ERROR_HANDLING_DOCS],
    };
  } catch {
    // The shared `api` client's response interceptor may already have
    // surfaced a global error toast for a genuine failure; we still return
    // a friendly in-dock message so the transcript is self-explanatory.
    return {
      text: LOOKUP_FAILED_TEXT(errorId),
      citedDocs: [...ERROR_HANDLING_DOCS],
    };
  }
}
